'use client';

import dynamic from 'next/dynamic';
import { ReactNode, useRef, useState } from 'react';
import SearchBarLazyLoad from '../SearchBar/search-bar-lazy-load';
import CartTrigger from './cartTrigger';

interface Props {
  navMenu: ReactNode;
  miniCartOpen: boolean;
  setMiniCartOpen: (open: boolean) => void;
  setShowNoItemDrawer: (open: boolean) => void;
}

const AccountTrigger = dynamic(() => import('./accountTrigger'), {
  ssr: false,
  loading: () => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='25'
      height='24'
      fill='none'
      viewBox='0 0 25 24'
      className='hidden h-auto lg_block lg_w-5'
    >
      <path
        className='fill-#403732 lg_group-hover_fill-80583F'
        d='M16.068 13.72c2.14-1.24 3.59-3.55 3.59-6.2 0-3.95-3.2-7.16-7.16-7.16a7.166 7.166 0 00-7.15 7.17c0 2.65 1.45 4.96 3.59 6.2a11.679 11.679 0 00-7.97 9.08c-.07.41.2.81.61.88.41.07.81-.2.88-.61a10.2 10.2 0 019.99-8.4h.12c4.92.03 9.11 3.55 9.99 8.4.07.37.39.62.75.62.04 0 .09 0 .14-.01.41-.08.69-.47.61-.88-.8-4.37-3.94-7.8-7.99-9.09zm-9.2-6.19c0-3.11 2.53-5.64 5.64-5.64s5.64 2.53 5.64 5.64c0 3.09-2.49 5.6-5.57 5.63h-.14c-3.08-.04-5.57-2.55-5.57-5.63z'
      />
      <path
        fill='#000'
        fillOpacity='0.2'
        d='M16.068 13.72c2.14-1.24 3.59-3.55 3.59-6.2 0-3.95-3.2-7.16-7.16-7.16a7.166 7.166 0 00-7.15 7.17c0 2.65 1.45 4.96 3.59 6.2a11.679 11.679 0 00-7.97 9.08c-.07.41.2.81.61.88.41.07.81-.2.88-.61a10.2 10.2 0 019.99-8.4h.12c4.92.03 9.11 3.55 9.99 8.4.07.37.39.62.75.62.04 0 .09 0 .14-.01.41-.08.69-.47.61-.88-.8-4.37-3.94-7.8-7.99-9.09zm-9.2-6.19c0-3.11 2.53-5.64 5.64-5.64s5.64 2.53 5.64 5.64c0 3.09-2.49 5.6-5.57 5.63h-.14c-3.08-.04-5.57-2.55-5.57-5.63z'
      />
    </svg>
  ),
});

const NavBar = (props: Props) => {
  const { navMenu, miniCartOpen, setMiniCartOpen, setShowNoItemDrawer } = props;

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);

  const handleSearchBarMouseDownOutside = e => {
    const searchBarTrigger = searchTriggerRef.current;

    if (!searchBarTrigger) return;

    if (!searchBarTrigger.contains(e.target)) {
      setIsSearchOpen(false);
    }
  };

  return (
    <div className='relative hidden grid-cols-[1fr,auto,1fr] gap-x-5 lg_grid'>
      <div></div>
      {navMenu}
      <div className='grid grid-cols-[max-content,max-content,max-content] items-center justify-end  gap-x-5 pr-10'>
        <button
          type='button'
          className='flex flex-none items-center gap-x-1.5'
          onClick={() => {
            setIsSearchOpen(x => !x);
          }}
          ref={searchTriggerRef}
        >
          {isSearchOpen ? (
            <div className='flex h-5 w-5 items-center justify-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                fill='none'
                viewBox='0 0 16 16'
              >
                <path
                  d='M15 15 1 1m14 0L1 15'
                  className='stroke-#403732 lg_group-hover_stroke-80583F'
                ></path>
                <path
                  stroke='#000'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeOpacity='0.2'
                  strokeWidth='1.5'
                  d='M15 15 1 1m14 0L1 15'
                ></path>
              </svg>
            </div>
          ) : (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              fill='none'
              viewBox='0 0 20 20'
            >
              <g
                clipPath='url(#clip0_17070_49213)'
                className='fill-#403732 lg_hover_fill-80583F'
              >
                <path d='M8.528 1.308c3.72 0 6.75 3.01 6.75 6.7s-3.03 6.7-6.75 6.7-6.75-3.01-6.75-6.7 3.03-6.7 6.75-6.7m0-1.3c-4.44 0-8.05 3.58-8.05 8s3.61 8 8.05 8 8.05-3.58 8.05-8c.01-4.42-3.6-8-8.05-8'></path>
                <path d='M18.597 19.99c-.17 0-.33-.06-.46-.19l-5.37-5.33a.65.65 0 0 1 0-.92c.25-.25.66-.26.92 0l5.37 5.33c.25.25.26.66 0 .92a.62.62 0 0 1-.46.19'></path>
              </g>
              <defs>
                <clipPath id='clip0_17070_49213'>
                  <path
                    fill='#fff'
                    d='M.478.008h18.77v19.98H.478z'
                  ></path>
                </clipPath>
              </defs>
            </svg>
          )}
          <span className='text-[15px] leading-[1.4] lg_hover_text-80583F'>Search</span>
        </button>
        <AccountTrigger />
        <div>
          <CartTrigger
            miniCartOpen={miniCartOpen}
            setMiniCartOpen={setMiniCartOpen}
            setShowNoItemDrawer={setShowNoItemDrawer}
          />
        </div>
        <SearchBarLazyLoad
          isSearchOpen={isSearchOpen}
          setIsSearchOpen={setIsSearchOpen}
          onMouseDownOutside={handleSearchBarMouseDownOutside}
        />
      </div>
    </div>
  );
};

export default NavBar;
